/* OVÉE HOMEPAGE (Oct 2026) — "a day in one scroll".
   Loaded only on the index template. Vanilla JS, no libraries.
   Everything here is progressive: without it the page renders in its final state. */
(function () {
  'use strict';

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var body = document.body;
  var root = document.documentElement;

  var SKY = {
    dawn: '#FEF6EF',
    day: '#FFFDF9',
    afternoon: '#F5EADB',
    dusk: '#F1E1CC',
    /* The night sections paint their own ink background. The page itself stays at dusk:
       turning the body navy would put navy text on navy in the light sections still
       visible above the night band (caught by the contrast check, 15 Oct). */
    night: '#F1E1CC'
  };

  /* ── The sky: each section declares a time of day; the page background
     follows whichever section crosses the middle of the screen. Sections
     without a marker inherit the one before (featured-blog, the footer). ── */
  function initSky() {
    if (!('IntersectionObserver' in window)) return;
    var sections = Array.prototype.slice.call(
      document.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group')
    );
    if (!sections.length) return;

    var current = 'dawn';
    sections.forEach(function (sec) {
      var marker = sec.matches('[data-oh-sky]') ? sec : sec.querySelector('[data-oh-sky]');
      if (marker) current = marker.getAttribute('data-oh-sky');
      sec.setAttribute('data-oh-sky-at', current);
    });

    function setSky(name) {
      if (!SKY[name] || body.getAttribute('data-oh-sky-now') === name) return;
      body.setAttribute('data-oh-sky-now', name);
      body.style.setProperty('--oh-sky', SKY[name]);
    }

    setSky('dawn');
    body.classList.add('oh-sky-on');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) setSky(entry.target.getAttribute('data-oh-sky-at'));
      });
    }, { rootMargin: '-50% 0px -50% 0px' });

    sections.forEach(function (sec) { io.observe(sec); });
  }

  /* ── Ingredient story: on desktop the sticky image follows the text block in
     the middle of the screen; on mobile a progress bar follows the swipe. ── */
  function initIngredients() {
    var wrap = document.querySelector('.oh-ing');
    if (!wrap) return;
    var items = Array.prototype.slice.call(wrap.querySelectorAll('.oh-ing__item'));
    var figures = Array.prototype.slice.call(wrap.querySelectorAll('.oh-ing__media figure'));
    var count = wrap.querySelector('.oh-ing__count');
    var list = wrap.querySelector('.oh-ing__list');
    var bar = wrap.querySelector('.oh-ing__progress span');
    var total = items.length;

    function activate(i) {
      items.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      figures.forEach(function (el, n) { el.classList.toggle('is-active', n === i); });
      if (count) count.textContent = ('0' + (i + 1)).slice(-2) + ' / ' + ('0' + total).slice(-2);
    }

    activate(0);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) activate(items.indexOf(entry.target));
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      items.forEach(function (el) { io.observe(el); });
    }

    if (list && bar) {
      var update = function () {
        var max = list.scrollWidth - list.clientWidth;
        var p = max > 0 ? list.scrollLeft / max : 0;
        // the bar is 1/total wide (CSS), so (total - 1) widths takes it to the end
        bar.style.transform = 'translateX(' + (p * (total - 1) * 100) + '%)';
      };
      list.addEventListener('scroll', update, { passive: true });
      update();
    }
  }

  /* ── Magnetic hero CTA: pulls up to 4px toward a fine pointer within 60px ── */
  function initMagnet() {
    if (reduce || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    document.querySelectorAll('[data-oh-magnet]').forEach(function (btn) {
      var raf = 0;
      document.addEventListener('mousemove', function (e) {
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = 0;
          var r = btn.getBoundingClientRect();
          var cx = r.left + r.width / 2;
          var cy = r.top + r.height / 2;
          var dx = e.clientX - cx;
          var dy = e.clientY - cy;
          var reach = Math.max(r.width, r.height) / 2 + 60;
          if (Math.abs(dx) < reach && Math.abs(dy) < reach) {
            btn.style.transform = 'translate(' + (dx / reach * 4).toFixed(2) + 'px,' + (dy / reach * 4).toFixed(2) + 'px)';
          } else if (btn.style.transform) {
            btn.style.transform = '';
          }
        });
      }, { passive: true });
    });
  }

  /* ── Sticky bar visibility → lift the chat bubble; cart count bump on add ── */
  function initBarState() {
    var bar = document.getElementById('ovee-sticky-bar');
    if (bar && 'MutationObserver' in window) {
      new MutationObserver(function () {
        body.classList.toggle('oh-bar-on', bar.classList.contains('is-visible'));
      }).observe(bar, { attributes: true, attributeFilter: ['class'] });
    }

    /* PUB_SUB_EVENTS is a top-level const in constants.js: a global binding, not a window property */
    var events = null;
    try { events = PUB_SUB_EVENTS; } catch (e) { events = null; }
    if (typeof window.subscribe === 'function' && events && !reduce) {
      window.subscribe(events.cartUpdate, function () {
        var icon = document.getElementById('cart-icon-bubble');
        if (!icon) return;
        icon.classList.remove('oh-cart-bump');
        void icon.offsetWidth;
        icon.classList.add('oh-cart-bump');
      });
    }
  }

  /* ── Hero sky cycle: a pause / play toggle (WCAG 2.2.2 — the cycle loops) ── */
  function initHeroPause() {
    if (reduce) return;
    document.querySelectorAll('.oh-hero').forEach(function (stage) {
      var btn = stage.querySelector('.oh-hero__pause');
      if (!btn) return;
      var label = btn.querySelector('.visually-hidden');
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var paused = stage.classList.toggle('is-paused');
        btn.setAttribute('aria-pressed', String(paused));
        if (label) label.textContent = paused ? 'Play the sunrise animation' : 'Pause the sunrise animation';
      });
    });
  }

  function boot() {
    body.classList.add('oh-home');
    initHeroPause();
    initSky();
    initIngredients();
    initMagnet();
    initBarState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
